// ui/src/components/padrekko/qc/FindingAuditTrail.jsx
//
// The whole record of one finding, as a vertical timeline.
//
// ── WHY AN OVERLAY AND NOT A CARD SECTION ────────────────────────────────────
//
// The QC panel column is 370px wide (MapPage.jsx: `calc(20rem + 50px)`). A row
// carrying time, actor, org, role badge, event and what changed does not fit in
// 370px, and abbreviating it until it does is how a record turns back into a
// pill. The expanded card body is also already seven sections deep — banner,
// analysis, chips, provenance chain, validation, completion, attachments,
// notes — and a ten-event trail inserted mid-way pushes the notes a reader is
// looking for off the bottom.
//
// So it opens over the map, at the width the content needs. Same shell as
// ParticipantsModal so it behaves like the other overlays in this surface.
//
// ── WHO SEES IT ──────────────────────────────────────────────────────────────
//
// Everyone. There is no caseWriteable gate anywhere in this file, deliberately.
// A read-only viewer — an operator after hand-off, a customer after closing —
// is exactly the reader an audit trail is FOR. Gating the record on the ability
// to change it would mean only the people who can still edit the finding may
// see who edited it.

import { useEffect } from "react";
import { X, ShieldCheck, ShieldAlert } from "lucide-react";
import { auditRows } from "./auditTrail";

const MICRO = "text-[10px] px-1.5 py-0.5 rounded border font-medium whitespace-nowrap";

function fmt(at) {
  if (!at) return "—";
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return String(at);
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}

/** Role badge — colour AND label, never colour alone. */
function RoleBadge({ badge }) {
  return <span className={`${MICRO} ${badge.tone}`}>{badge.label}</span>;
}

/** VERIFY → NON_COMPLIANT. The arrow is what makes this an argument. */
function Transition({ transition }) {
  if (!transition) return null;
  const same = transition.from === transition.to;
  return (
    <span className="inline-flex items-center gap-1.5 font-mono text-[11px]">
      <span className="text-slate-400">{transition.from}</span>
      <span className="text-slate-500" aria-hidden="true">→</span>
      <span className={same ? "text-slate-400" : "text-slate-100 font-semibold"}>
        {transition.to}
      </span>
      {same && <span className="text-slate-500 not-italic">(unchanged)</span>}
    </span>
  );
}

function Detail({ row }) {
  const d = row.detail;
  const bits = [];

  if (d?.kind === "detected") {
    if (d.what) bits.push(<span key="w" className="text-slate-200">{d.what}</span>);
    if (d.engine) bits.push(<span key="e" className="text-slate-500 font-mono text-[10px]">{d.engine}</span>);
  }
  if (d?.kind === "polygon") {
    bits.push(
      <span key="p" className="text-slate-400">
        Geometry changed{d.hasSnapshot ? " · previous outline retained" : ""}
      </span>
    );
  }
  if (d?.kind === "reanalysis") {
    if (row.transition) bits.push(<Transition key="t" transition={row.transition} />);
    if (d.correction) {
      bits.push(
        <span key="c" className="text-slate-300 italic">“{d.correction}”</span>
      );
    }
  }
  if (row.validation) {
    // A superseded validation is not struck through or greyed into
    // insignificance — it happened, and it is part of the record. It is
    // LABELLED, so the difference is readable rather than inferred from tone.
    const { known, isCurrent } = row.validation;
    if (!known) {
      bits.push(<span key="v" className="text-slate-500">Currency not reported</span>);
    } else if (isCurrent) {
      bits.push(
        <span key="v" className={`${MICRO} bg-emerald-500/15 border-emerald-400/40 text-emerald-200 inline-flex items-center gap-1`}>
          <ShieldCheck className="w-3 h-3" aria-hidden="true" />
          Still current
        </span>
      );
    } else {
      bits.push(
        <span key="v" className={`${MICRO} bg-amber-500/15 border-amber-400/40 text-amber-200 inline-flex items-center gap-1`}>
          <ShieldAlert className="w-3 h-3" aria-hidden="true" />
          Superseded by a later change
        </span>
      );
    }
  }
  if (row.note) {
    bits.push(<span key="n" className="text-slate-300">“{row.note}”</span>);
  }

  if (!bits.length) return <span className="text-slate-600">—</span>;
  return <span className="flex flex-wrap items-center gap-x-2 gap-y-1">{bits}</span>;
}

export default function FindingAuditTrail({ finding, padName, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const rows = auditRows(finding);

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Finding audit trail"
    >
      <div
        className="w-full max-w-3xl max-h-[85vh] flex flex-col bg-bg-soft border border-slate-700 rounded-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-3 px-4 py-3 border-b border-slate-800 shrink-0">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-slate-100">Audit trail</h2>
            <p className="text-[11px] text-slate-400 truncate">
              {padName ? `${padName} · ` : ""}Finding #{finding?.idx ?? "?"} ·{" "}
              {rows.length} event{rows.length === 1 ? "" : "s"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close audit trail"
            className="shrink-0 p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-slate-200"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="overflow-y-auto px-4 py-3">
          {rows.length === 0 ? (
            <p className="text-[12px] text-slate-500 py-6 text-center">
              This finding carries no recorded history.
            </p>
          ) : (
            <ol className="relative">
              {/* The spine. Decorative — the <ol> already carries the ordering. */}
              <span
                className="absolute left-[5px] top-1 bottom-1 w-px bg-slate-700/70"
                aria-hidden="true"
              />
              {rows.map((row) => (
                <li key={row.key} className="relative pl-6 pb-4 last:pb-0">
                  <span
                    className={`absolute left-0 top-1.5 w-[11px] h-[11px] rounded-full border-2 border-bg-soft ${
                      row.recognised ? "bg-slate-400" : "bg-slate-600"
                    }`}
                    aria-hidden="true"
                  />
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <time className="text-[11px] font-mono text-slate-500 tabular-nums">
                      {fmt(row.at)}
                    </time>
                    <RoleBadge badge={row.badge} />
                    {row.actorName ? (
                      // A resolved name is marked, a snapshotted one is not.
                      // Marking the exception rather than every row keeps the
                      // timeline readable while still letting a reader see
                      // which names could move under a rename.
                      <span
                        className={`text-[12px] text-slate-200${
                          row.nameSource === "resolved"
                            ? " underline decoration-dotted decoration-slate-600 underline-offset-2"
                            : ""
                        }`}
                        title={
                          row.nameSource === "resolved"
                            ? "Resolved from this person's profile as it stands now — this entry recorded only their id, so a rename would change this name."
                            : "Recorded at the time of the event. A later rename cannot change it."
                        }
                      >
                        {row.actorName}
                      </span>
                    ) : row.hasActorId ? (
                      <span className="text-[12px] text-slate-500">Unknown actor</span>
                    ) : null}
                    {row.orgName && (
                      <span className="text-[11px] text-slate-400">{row.orgName}</span>
                    )}
                    <span className="text-[12px] font-medium text-slate-100">{row.label}</span>
                    {row.stage && (
                      <span className="text-[10px] font-mono text-slate-500">
                        at {row.stage}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-[12px]">
                    <Detail row={row} />
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>

        <footer className="px-4 py-2 border-t border-slate-800 shrink-0">
          {/* Not a disclaimer — the one thing a reader must know to weigh the
              names above, and now stated PER ROW rather than by event type.
              Every writer snapshots the actor, so from here on names cannot
              move; entries written before that still carry only ids and are
              resolved on read. Which is which comes from the server's
              actor_name_source, because the two are indistinguishable in the
              payload once resolved. See lib/padrekko-qc/actorNames.js. */}
          {rows.some((r) => r.nameSource === "resolved") ? (
            <p className="text-[10px] text-slate-500 leading-relaxed">
              <span className="underline decoration-dotted decoration-slate-600 underline-offset-2">
                Underlined names
              </span>{" "}
              were resolved from the person&rsquo;s current profile — those entries
              recorded only an id, so a rename would change them. Every other name
              is as it was recorded at the time.
            </p>
          ) : (
            <p className="text-[10px] text-slate-500 leading-relaxed">
              Every name here was recorded at the time of the event. A later rename
              cannot change it.
            </p>
          )}
        </footer>
      </div>
    </div>
  );
}
